/**
 * Zalo Personal domain facade.
 *
 * UI không được gọi `act` hoặc tự bóc envelope provider. Tất cả khác biệt của
 * API Zalo Personal tập trung ở đây để khi BE đổi response/endpoint chỉ sửa
 * một chỗ.
 */
import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, finalize, firstValueFrom, forkJoin, map, Observable, of, shareReplay, switchMap, timeout} from 'rxjs';
import {
  getZlpsResponseError,
  getZlpsResponseItems,
  isZlpsResponseSuccessful,
  IChangeFriendAliasParams,
  IChangeFriendAliasResponse,
  IZlpsDeleteChatParams,
  IZlpsDisperseGroupParams,
  IZlpsFriendEvent,
  IZlpsGroupLinkResult,
  IZlpsGroupMember,
  IZlpsGroupSetting,
  IZlpsRelatedFriendGroupData,
  IZlpsUpdateGroupParams,
  IZlpsUserData,
  normalizeZlpsUserData,
  unwrapZlpsResponseData,
  ZaloPersonalApiService,
} from '@shared/services/api/conversation/zalo-personal-api.service';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {LoginApiService} from '@shared/services/api/login-page';
import { ZaloPersonalUserPickerSource } from '@features/conversation/models/zalo-personal-ui.model';

export interface IZaloPersonalGroupInfo extends IZlpsUserData {
  groupId: string;
  adminIds: string[];
  memVerList: string[];
  setting: IZlpsGroupSetting;
}

export interface IZaloPersonalGroupPreview {
  memberCount: number;
  groupAvatar?: string;
  avatars: IZlpsGroupMember[];
  memberIds: string[];
  expiresAt: number;
}

export interface IGroupPreviewRequest {
  pageId: string;
  groupId: string;
  conversationId: string;
  recentSenderIds: string[];
  priority: boolean;
  visible: boolean;
}

interface IQueuedGroupPreview {
  request: IGroupPreviewRequest;
  memberCount: number;
  memberIds: string[];
  allMemberIds: string[];
  version: number;
}

export interface ICreateZaloPersonalGroupInput {
  pageId: string;
  name: string;
  memberIds: string[];
  avatarSource?: string;
}

export interface IZaloPersonalCardPayload {
  threadId: string;
  type: 0 | 1;
  options: {
    userId?: string;
    phoneNumber?: string;
  };
}

export interface IZaloPersonalGroupSettingsInput {
  enableMsgHistory: boolean;
  signAdminMsg: boolean;
  joinAppr: boolean;
  setTopicOnly: boolean;
  blockName: boolean;
  lockCreatePost: boolean;
  lockCreatePoll: boolean;
  lockSendMsg: boolean;
}

/** Loại recipient cho sendCard API */
export const ZALO_PERSONAL_CARD_TYPE = {
  USER: 0,
  GROUP: 1,
} as const;

@Injectable({providedIn: 'root'})
export class ZaloPersonalService {
  private readonly api = inject(ZaloPersonalApiService);
  private readonly conversationApi = inject(ConversationApiService);
  private readonly loginApi = inject(LoginApiService);
  private static readonly GROUP_PREVIEW_STORAGE_KEY = 'vpage.zlps.group-preview.v1';
  private static readonly GROUP_PREVIEW_TTL = 24 * 60 * 60 * 1000;
  private static readonly GROUP_PREVIEW_ERROR_TTL = 15 * 60 * 1000;
  private static readonly GROUP_PREVIEW_LIMIT = 500;
  private readonly groupPreviews = new Map<string, IZaloPersonalGroupPreview>();
  private readonly groupPreviewRequests = new Map<string, IGroupPreviewRequest>();
  private readonly pendingGroupPreviews = new Map<string, IGroupPreviewRequest>();
  // Giữ token đến khi batch profile hoàn tất để cuộn/realtime không gọi trùng.
  private readonly groupPreviewInFlight = new Map<string, number>();
  private readonly queuedGroupPreviews = new Map<string, IQueuedGroupPreview>();
  private readonly loadingGroupPreviewMembers = new Map<string, IQueuedGroupPreview>();
  private readonly groupInfoRequests = new Map<string, Observable<IZaloPersonalGroupInfo>>();
  private readonly recentGroupSenders = new Map<string, string[]>();
  private readonly groupPreviewsSubject = new BehaviorSubject<Record<string, IZaloPersonalGroupPreview>>({});
  readonly groupPreviews$ = this.groupPreviewsSubject.asObservable();
  private groupPreviewTimer?: ReturnType<typeof setTimeout>;
  private groupPreviewBatchTimer?: ReturnType<typeof setTimeout>;
  private groupPreviewStorageTimer?: ReturnType<typeof setTimeout>;
  private groupPreviewBatchRunning = false;
  private runningGroupInfoRequests = 0;
  private groupPreviewVersion = 0;
  private activeGroupPreviewKey: string | null = null;

  constructor() {
    this.restoreGroupPreviewCache();
  }

  setActiveGroupPreview(pageId?: string, groupId?: string): void {
    const previousKey = this.activeGroupPreviewKey;
    this.activeGroupPreviewKey = pageId && groupId ? this.groupPreviewKey(pageId, groupId) : null;
    if (previousKey && previousKey !== this.activeGroupPreviewKey) {
      const previous = this.groupPreviewRequests.get(previousKey);
      if (previous) previous.priority = false;
    }
    const current = this.activeGroupPreviewKey ? this.groupPreviewRequests.get(this.activeGroupPreviewKey) : null;
    if (current) {
      current.priority = true;
      this.scheduleGroupPreviewRequests();
    }
  }

  requestGroupPreview(request: IGroupPreviewRequest): void {
    if (!request.pageId || !request.groupId || !request.conversationId) return;
    const key = this.groupPreviewKey(request.pageId, request.groupId);
    if (!request.visible) return;
    const previous = this.groupPreviewRequests.get(key);
    request = {...request, priority: key === this.activeGroupPreviewKey,
      recentSenderIds: this.uniqueGroupSenders([...request.recentSenderIds, ...(previous?.recentSenderIds || [])])};
    this.groupPreviewRequests.set(key, request);
    const cached = this.groupPreviews.get(key);
    // Cache cũ chưa biết avatar nhóm cần được cập nhật một lần.
    if (cached && cached.expiresAt > Date.now() && (cached.groupAvatar !== undefined || cached.memberCount === 0)) {
      this.touchGroupPreview(key, cached);
      this.persistGroupPreviews();
      return;
    }
    if (cached) {
      this.groupPreviews.delete(key);
      this.publishGroupPreviews();
    }
    if (!this.groupPreviewInFlight.has(key)) this.pendingGroupPreviews.set(key, request);
    this.scheduleGroupPreviewRequests();
  }

  releaseGroupPreview(pageId: string, groupId: string): void {
    const key = this.groupPreviewKey(pageId, groupId);
    this.groupPreviewRequests.delete(key);
    this.pendingGroupPreviews.delete(key);
    // Giữ request đã gửi/đang gom batch; batch chỉ gửi tiếp nếu item còn nhìn thấy.
  }

  rememberGroupSenders(pageId: string, groupId: string, senderIds: string[]): void {
    const key = this.groupPreviewKey(pageId, groupId);
    const senders = this.uniqueGroupSenders([...senderIds, ...(this.recentGroupSenders.get(key) || [])]);
    this.recentGroupSenders.delete(key);
    this.recentGroupSenders.set(key, senders);
    if (this.recentGroupSenders.size > ZaloPersonalService.GROUP_PREVIEW_LIMIT) {
      this.recentGroupSenders.delete(this.recentGroupSenders.keys().next().value!);
    }
    const request = this.groupPreviewRequests.get(key);
    if (request) request.recentSenderIds = senders;
  }

  // Dùng lại groupInfo mà ChatCore vừa tải, ưu tiên người vừa nhắn khi mở nhóm.
  seedGroupPreview(request: IGroupPreviewRequest, info: IZaloPersonalGroupInfo): void {
    this.rememberGroupSenders(request.pageId, request.groupId, request.recentSenderIds);
    const key = this.groupPreviewKey(request.pageId, request.groupId);
    const previous = this.groupPreviewRequests.get(key);
    request = {...request, priority: key === this.activeGroupPreviewKey, visible: previous?.visible || request.visible};
    const groupAvatar = this.getGroupAvatarUrl(info.avatar) || this.getGroupAvatarUrl(info.avt);
    if (!request.visible && !groupAvatar) return;
    if (request.visible) this.groupPreviewRequests.set(key, request);
    const memberIds = groupAvatar ? [] : this.selectGroupPreviewMembers(request, this.extractMemberIds(info.memVerList));
    const cached = this.groupPreviews.get(key);
    if (cached && cached.expiresAt > Date.now() && cached.groupAvatar === groupAvatar && cached.memberCount === info.memVerList.length
      && memberIds.every((id, index) => id === cached.memberIds[index]) && memberIds.length === cached.memberIds.length) return;
    const loading = this.queuedGroupPreviews.get(key) || this.loadingGroupPreviewMembers.get(key);
    if (loading && this.groupPreviewInFlight.get(key) === loading.version && loading.memberCount === info.memVerList.length
      && memberIds.length === loading.memberIds.length && memberIds.every((id, index) => id === loading.memberIds[index])) return;
    this.pendingGroupPreviews.delete(key);
    const version = ++this.groupPreviewVersion;
    this.groupPreviewInFlight.set(key, version);
    this.queueGroupPreviewMembers(key, request, info, version);
  }

  invalidateGroupPreview(pageId: string, groupId: string): void {
    if (!pageId || !groupId) return;
    const key = this.groupPreviewKey(pageId, groupId);
    this.groupPreviewInFlight.delete(key);
    this.groupInfoRequests.delete(key);
    this.groupPreviews.delete(key);
    this.queuedGroupPreviews.delete(key);
    this.publishGroupPreviews();
    this.persistGroupPreviews();
    const request = this.groupPreviewRequests.get(key);
    if (request) this.requestGroupPreview(request);
  }

  private uniqueGroupSenders(ids: string[]): string[] {
    return [...new Set(ids.map(id => this.normalizeMemberId(id)).filter(Boolean))].slice(0, 20);
  }

  private selectGroupPreviewMembers(request: IGroupPreviewRequest, memberIds: string[]): string[] {
    const memberSet = new Set(memberIds);
    const recent = this.uniqueGroupSenders([...request.recentSenderIds, ...(this.recentGroupSenders.get(this.groupPreviewKey(request.pageId, request.groupId)) || [])]);
    return [...new Set([...recent.filter(id => memberSet.has(id)), ...memberIds])].slice(0, 3);
  }

  private scheduleGroupPreviewRequests(): void {
    if (this.groupPreviewTimer) return;
    // Gom các callback IntersectionObserver trước khi chọn nhóm ưu tiên.
    this.groupPreviewTimer = setTimeout(() => {
      this.groupPreviewTimer = undefined;
      this.processGroupPreviewRequests();
    }, 0);
  }

  private processGroupPreviewRequests(): void {
    const requests = [...this.pendingGroupPreviews.entries()]
      .sort(([a], [b]) => Number(b === this.activeGroupPreviewKey) - Number(a === this.activeGroupPreviewKey));
    for (const [key, request] of requests) {
      if (this.runningGroupInfoRequests >= 3) break;
      this.pendingGroupPreviews.delete(key);
      const version = ++this.groupPreviewVersion;
      this.groupPreviewInFlight.set(key, version);
      this.runningGroupInfoRequests++;
      void this.loadGroupPreviewInfo(key, request, version);
    }
  }

  private async loadGroupPreviewInfo(key: string, request: IGroupPreviewRequest, version: number): Promise<void> {
    try {
      const info = await firstValueFrom(this.getGroupInfo(request.pageId, request.groupId));
      if (this.groupPreviewInFlight.get(key) !== version || !this.groupPreviewRequests.has(key)) return;
      const historySenders = this.getGroupAvatarUrl(info.avatar) || this.getGroupAvatarUrl(info.avt)
        ? [] : await this.loadRecentGroupSenders(request);
      if (this.groupPreviewInFlight.get(key) !== version) return;
      const latestRequest = this.groupPreviewRequests.get(key);
      if (!latestRequest) {
        this.groupPreviewInFlight.delete(key);
        return;
      }
      latestRequest.recentSenderIds = this.uniqueGroupSenders([...latestRequest.recentSenderIds, ...historySenders]);
      this.queueGroupPreviewMembers(key, latestRequest, info, version);
    } catch {
      if (this.groupPreviewInFlight.get(key) === version) {
        this.setGroupPreview(key, {memberCount: 0, avatars: [], memberIds: [], expiresAt: Date.now() + ZaloPersonalService.GROUP_PREVIEW_ERROR_TTL});
        this.groupPreviewInFlight.delete(key);
      }
    } finally {
      if (!this.groupPreviewRequests.has(key) && this.groupPreviewInFlight.get(key) === version) this.groupPreviewInFlight.delete(key);
      this.runningGroupInfoRequests--;
      this.processGroupPreviewRequests();
    }
  }

  private async loadRecentGroupSenders(request: IGroupPreviewRequest): Promise<string[]> {
    if (request.priority) return request.recentSenderIds;
    try {
      const response = await firstValueFrom(this.conversationApi.getMessages<{
        messages?: {items?: Array<{senderId?: string; createdAt?: number; createdTime?: number; attachments?: Array<{type?: string}>}>};
      }>({cid: request.conversationId, pageId: request.pageId, limit: 30}).pipe(timeout(15000)));
      if (response.code !== 1) return [];
      const messages = response.data?.messages?.items || [];
      const pageUserId = this.normalizeMemberId(request.pageId.replace('zlp_', ''));
      return this.uniqueGroupSenders(messages.slice().sort((a, b) =>
        (b.createdAt || (b.createdTime || 0) * 1000) - (a.createdAt || (a.createdTime || 0) * 1000))
        .filter(message => !message.attachments?.some(attachment => attachment.type === 'emote'))
        .map(message => String(message.senderId || ''))
        .filter(id => id !== request.pageId && this.normalizeMemberId(id) !== pageUserId));
    } catch {
      return [];
    }
  }

  private queueGroupPreviewMembers(key: string, request: IGroupPreviewRequest, info: IZaloPersonalGroupInfo, version: number): void {
    const groupAvatar = this.getGroupAvatarUrl(info.avatar) || this.getGroupAvatarUrl(info.avt);
    if (groupAvatar) {
      this.queuedGroupPreviews.delete(key);
      this.setGroupPreview(key, {memberCount: info.memVerList.length, groupAvatar, avatars: [], memberIds: [],
        expiresAt: Date.now() + ZaloPersonalService.GROUP_PREVIEW_TTL});
      this.groupPreviewInFlight.delete(key);
      return;
    }
    const allMemberIds = this.extractMemberIds(info.memVerList);
    const memberIds = this.selectGroupPreviewMembers(request, allMemberIds);
    if (!memberIds.length) {
      this.setGroupPreview(key, {memberCount: info.memVerList.length, groupAvatar: '', avatars: [], memberIds, expiresAt: Date.now() + ZaloPersonalService.GROUP_PREVIEW_ERROR_TTL});
      this.groupPreviewInFlight.delete(key);
      return;
    }
    this.queuedGroupPreviews.set(key, {request, memberCount: info.memVerList.length, memberIds, allMemberIds, version});
    if (!this.groupPreviewBatchTimer) {
      this.groupPreviewBatchTimer = setTimeout(() => {
        this.groupPreviewBatchTimer = undefined;
        void this.flushGroupPreviewBatch();
      }, 100);
    }
  }

  private async flushGroupPreviewBatch(): Promise<void> {
    if (this.groupPreviewBatchRunning) return;
    this.groupPreviewBatchRunning = true;
    const byPage = new Map<string, Array<[string, IQueuedGroupPreview]>>();
    for (const [key, group] of this.queuedGroupPreviews) {
      group.memberIds = this.selectGroupPreviewMembers(group.request, group.allMemberIds);
      const groups = byPage.get(group.request.pageId) || [];
      groups.push([key, group]);
      byPage.set(group.request.pageId, groups);
    }
    this.queuedGroupPreviews.clear();
    const pages = [...byPage.entries()].sort(([, a], [, b]) =>
      Number(b.some(([key]) => key === this.activeGroupPreviewKey)) - Number(a.some(([key]) => key === this.activeGroupPreviewKey)));
    try {
      for (const [pageId, queuedGroups] of pages) {
        const groups = queuedGroups.filter(([key, group]) => {
          if (this.groupPreviewInFlight.get(key) !== group.version) return false;
          if (this.groupPreviewRequests.has(key)) return true;
          this.groupPreviewInFlight.delete(key);
          return false;
        });
        if (!groups.length) continue;
        for (const [key, group] of groups) this.loadingGroupPreviewMembers.set(key, group);
        const ids = [...new Set(groups.flatMap(([, group]) => group.memberIds))];
        let members: IZlpsGroupMember[] = [];
        try {
          members = await firstValueFrom(this.getGroupMembers(pageId, ids).pipe(timeout(35000)));
        } catch { /* Giữ số thành viên đã lấy được và thử lại sau 15 phút. */ }
        const memberMap = new Map(members.map(member => {
          const normalized = this.toGroupMember(member);
          return [this.normalizeMemberId(member.userId ?? member.fanpageUserId ?? member.uid), normalized];
        }));
        for (const [key, group] of groups) {
          if (this.loadingGroupPreviewMembers.get(key) === group) this.loadingGroupPreviewMembers.delete(key);
          if (this.groupPreviewInFlight.get(key) !== group.version) continue;
          const avatars = group.memberIds.map(id => memberMap.get(id))
            .filter((member): member is IZlpsGroupMember => !!member?.avatar)
            .map(member => ({userId: member.userId, avatar: member.avatar, displayName: member.displayName}));
          this.setGroupPreview(key, {
            memberCount: group.memberCount, groupAvatar: '', avatars, memberIds: group.memberIds,
            expiresAt: Date.now() + (avatars.length === group.memberIds.length
              ? ZaloPersonalService.GROUP_PREVIEW_TTL : ZaloPersonalService.GROUP_PREVIEW_ERROR_TTL),
          });
          this.groupPreviewInFlight.delete(key);
        }
      }
    } finally {
      this.groupPreviewBatchRunning = false;
      if (this.queuedGroupPreviews.size && !this.groupPreviewBatchTimer) {
        this.groupPreviewBatchTimer = setTimeout(() => {
          this.groupPreviewBatchTimer = undefined;
          void this.flushGroupPreviewBatch();
        }, 100);
      }
    }
  }

  private setGroupPreview(key: string, preview: IZaloPersonalGroupPreview): void {
    this.touchGroupPreview(key, preview);
    while (this.groupPreviews.size > ZaloPersonalService.GROUP_PREVIEW_LIMIT) {
      this.groupPreviews.delete(this.groupPreviews.keys().next().value!);
    }
    this.publishGroupPreviews();
    this.persistGroupPreviews();
  }

  private touchGroupPreview(key: string, preview: IZaloPersonalGroupPreview): void {
    this.groupPreviews.delete(key);
    this.groupPreviews.set(key, preview);
  }

  private publishGroupPreviews(): void {
    this.groupPreviewsSubject.next(Object.fromEntries(this.groupPreviews));
  }

  private restoreGroupPreviewCache(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const stored: unknown = JSON.parse(localStorage.getItem(ZaloPersonalService.GROUP_PREVIEW_STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return;
      for (const entry of stored.slice(-ZaloPersonalService.GROUP_PREVIEW_LIMIT)) {
        if (!Array.isArray(entry) || entry.length !== 2) continue;
        const [key, preview] = entry as [string, IZaloPersonalGroupPreview];
        if (typeof key === 'string' && preview && Number.isSafeInteger(preview.memberCount) && preview.memberCount >= 0
          && Number.isFinite(preview.expiresAt) && preview.expiresAt > Date.now()
          && Array.isArray(preview.avatars) && Array.isArray(preview.memberIds)
          && (preview.groupAvatar === undefined || typeof preview.groupAvatar === 'string')
          && preview.avatars.every(member => member && typeof member.userId === 'string' && typeof member.avatar === 'string')
          && preview.memberIds.every(id => typeof id === 'string')) {
          this.groupPreviews.set(key, {...preview, avatars: preview.avatars.slice(0, 3), memberIds: preview.memberIds.slice(0, 3)});
        }
      }
      this.publishGroupPreviews();
    } catch { /* Storage bị chặn hoặc dữ liệu cũ hỏng: dùng memory cache. */ }
  }

  private persistGroupPreviews(): void {
    if (this.groupPreviewStorageTimer) return;
    this.groupPreviewStorageTimer = setTimeout(() => {
      this.groupPreviewStorageTimer = undefined;
      try {
        if (typeof localStorage === 'undefined') return;
        localStorage.setItem(ZaloPersonalService.GROUP_PREVIEW_STORAGE_KEY, JSON.stringify([...this.groupPreviews]));
      } catch { /* Storage đầy: memory cache vẫn render được. */ }
    }, 100);
  }

  private groupPreviewKey(pageId: string, groupId: string): string {
    return `${pageId}::${groupId}`;
  }

  getGroupAvatarUrl(avatar: unknown): string {
    return typeof avatar === 'string' && !avatar.includes('default_avatar') ? avatar.trim() : '';
  }

  findUserByPhone(pageId: string, phone: string): Observable<IZlpsUserData | null> {
    return this.query(pageId, 'search_user_by_phone', {phone}).pipe(map(response => this.toUser(response)));
  }

  getUserInfo(pageId: string, userId: string | string[]): Observable<IZlpsUserData | null> {
    return this.query(pageId, 'get_user_info', {userId}).pipe(map(response => this.toUser(response)));
  }

  getUsersInfo(pageId: string, userIds: string[]): Observable<IZlpsGroupMember[]> {
    if (!userIds.length) return of([]);
    return this.query(pageId, 'get_user_info', {userId: userIds}).pipe(map(response => this.toMembers(response)));
  }

  getGroupInfo(pageId: string, groupId: string): Observable<IZaloPersonalGroupInfo> {
    const key = this.groupPreviewKey(pageId, groupId);
    const existing = this.groupInfoRequests.get(key);
    if (existing) return existing;
    const request = this.query(pageId, 'get_group_info', {userId: groupId}).pipe(timeout(35000), map(response => {
      // Chặn cả subscriber của ChatCore dùng response trước khi nhóm bị invalidated.
      if (this.groupInfoRequests.get(key) !== request) throw new Error('conversation.toast.genericErrorRetry');
      const group = normalizeZlpsUserData(unwrapZlpsResponseData(response)) ?? {};
      const error = getZlpsResponseError(response);
      if (!isZlpsResponseSuccessful(response) || error || !Array.isArray(group.memVerList)) {
        throw new Error(error || 'conversation.toast.zaloInfoNotFound');
      }
      return {
        ...group,
        groupId: group.groupId || groupId,
        adminIds: Array.isArray(group.adminIds) ? group.adminIds : [],
        memVerList: Array.isArray(group.memVerList) ? group.memVerList : [],
        setting: group.setting ?? {},
      };
    }), finalize(() => {
      if (this.groupInfoRequests.get(key) === request) this.groupInfoRequests.delete(key);
    }), shareReplay({bufferSize: 1, refCount: true}));
    this.groupInfoRequests.set(key, request);
    return request;
  }

  getGroupMembers(pageId: string, memberIds: string[]): Observable<IZlpsGroupMember[]> {
    if (!memberIds.length) return of([]);
    return this.query(pageId, 'get_group_members_info', {userId: memberIds}).pipe(
      map(response => {
        this.assertSuccessful(response);
        return this.toMembers(response);
      }),
    );
  }

  getFriendRequestStatus(pageId: string, userId: string): Observable<Record<string, unknown> | null> {
    return this.query(pageId, 'get_friend_request_status', {userId}).pipe(
      map(response => unwrapZlpsResponseData<Record<string, unknown>>(response)),
    );
  }

  listRecentlyContacted(pageId: string): Observable<IZlpsGroupMember[]> {
    return this.query(pageId, 'user_recently', {type: 2}).pipe(map(response => this.toMembers(response)));
  }

  listFriends(pageId: string): Observable<IZlpsGroupMember[]> {
    return this.query(pageId, 'all_friends').pipe(map(response => this.toMembers(response)));
  }

  /** Lấy ID nhóm chung trước, rồi lấy thông tin các nhóm trong một request. */
  getCommonGroups(pageId: string, friendId: string): Observable<IZlpsGroupMember[]> {
    return this.api.getRelatedFriendGroup({pageId, friendId}).pipe(
      timeout(35000),
      switchMap(response => {
        this.assertSuccessful(response);
        const data = unwrapZlpsResponseData<IZlpsRelatedFriendGroupData>(response);
        const related = data?.groupRelateds?.[friendId];
        if (!data?.groupRelateds || (related !== undefined && !Array.isArray(related))) {
          throw new Error('INVALID_RELATED_GROUPS_RESPONSE');
        }
        const groupIds = [...new Set((related ?? []).filter(id => typeof id === 'string' && !!id))];
        if (!groupIds.length) return of([]);
        return this.query(pageId, 'get_group_info', {userId: groupIds}).pipe(
          timeout(35000),
          map(result => {
            this.assertSuccessful(result);
            const groups = this.toMembers(result);
            return groupIds.map(groupId => groups.find(group => group.userId === groupId)
              ?? {userId: groupId, groupId, displayName: groupId});
          }),
        );
      }),
    );
  }

  listGroups(pageId: string): Observable<IZlpsGroupMember[]> {
    return this.query(pageId, 'all_groups').pipe(map(response => this.toMembers(response)));
  }

  listPickerUsers(
    pageId: string,
    source: ZaloPersonalUserPickerSource,
    conversationType?: number,
  ): Observable<IZlpsGroupMember[]> {
    return this.query(pageId, source, conversationType === undefined ? {} : {type: conversationType})
      .pipe(map(response => this.toMembers(response)));
  }

  listFriendRequests(pageId: string): Observable<IZlpsFriendEvent[]> {
    return this.api.listFriendEvent({pageIds: [pageId]}).pipe(
      map(response => getZlpsResponseItems<Record<string, unknown>>(response)
        .map(item => this.toFriendRequest(item, pageId))),
      // Provider đôi khi vẫn trả lời mời cũ sau khi hai bên đã là bạn. Xác minh
      // trạng thái hiện tại trước khi render để không cho thao tác nhầm.
      switchMap(items => items.length
        ? forkJoin(items.map(item => this.getFriendRequestStatus(pageId, item.userId).pipe(
          map(status => ({item, status})),
          // Không che lời mời nếu API kiểm tra riêng lẻ bị lỗi.
          catchError(() => of({item, status: null})),
        )))
        : of([]),
      ),
      map(results => results
        .filter(({status}) => Number(status?.['is_friend'] ?? 0) !== 1)
        .map(({item}) => item),
      ),
    );
  }

  sendFriendRequest(pageId: string, userId: string, text: string, silent = false): Observable<void> {
    return this.command(pageId, 'send_friend_request', {userId, text, silent});
  }

  acceptFriendRequest(pageId: string, userId: string): Observable<void> {
    return this.command(pageId, 'accept_friend_request', {userId});
  }

  rejectFriendRequest(pageId: string, userId: string): Observable<void> {
    return this.command(pageId, 'reject_friend_request', {userId});
  }

  createGroup(input: ICreateZaloPersonalGroupInput): Observable<void> {
    return this.command(input.pageId, 'create_group', {
      groupName: input.name,
      avatarSource: input.avatarSource || '',
      memberIds: input.memberIds.join(','),
    });
  }

  sendCards(pageId: string, cards: readonly IZaloPersonalCardPayload[]): Observable<void> {
    return this.api.sendCard({pageId, targetUserId: '', cardsInfo: JSON.stringify(cards)}).pipe(
      map(response => this.assertSuccessful(response)),
    );
  }

  addGroupMembers(pageId: string, groupId: string, memberIds: string[]): Observable<void> {
    return this.command(pageId, 'add_user_to_group', {groupId, userId: memberIds.join(',')}).pipe(
      map(() => this.invalidateGroupPreview(pageId, groupId)),
    );
  }

  removeGroupMember(pageId: string, groupId: string, userId: string): Observable<void> {
    return this.command(pageId, 'remove_user_from_group', {groupId, userId}).pipe(
      map(() => this.invalidateGroupPreview(pageId, groupId)),
    );
  }

  reviewPendingMember(pageId: string, groupId: string, userId: string, isApprove: boolean): Observable<void> {
    return this.command(pageId, 'review_pending_member_request', {
      userId: groupId,
      payload: JSON.stringify({members: userId, isApprove}),
    }).pipe(map(() => isApprove ? this.invalidateGroupPreview(pageId, groupId) : undefined));
  }

  setUserBlocked(pageId: string, userId: string, blocked: boolean): Observable<void> {
    return this.command(pageId, blocked ? 'block_user' : 'unblock_user', {userId});
  }

  leaveGroup(pageId: string, groupId: string, silent: boolean): Observable<void> {
    return this.command(pageId, 'leave_group', {userId: groupId, silent}).pipe(
      map(() => this.invalidateGroupPreview(pageId, groupId)),
    );
  }

  getGroupLink(pageId: string, groupId: string): Observable<{link: string; enabled: 0 | 1}> {
    return this.query(pageId, 'get_group_link', {userId: groupId}).pipe(map(response => {
      const value = unwrapZlpsResponseData<IZlpsGroupLinkResult['data']>(response);
      return {link: value?.link ?? '', enabled: value?.enabled ?? 0};
    }));
  }

  setGroupLinkEnabled(pageId: string, groupId: string, enabled: boolean): Observable<{link: string; enabled: 0 | 1}> {
    return this.query(pageId, enabled ? 'enable_group_link' : 'disable_group_link', {userId: groupId}).pipe(map(response => {
      const value = unwrapZlpsResponseData<IZlpsGroupLinkResult['data']>(response);
      return {link: value?.link ?? '', enabled: value?.enabled ?? 0};
    }));
  }

  updateGroup(params: IZlpsUpdateGroupParams): Observable<unknown> {
    return this.api.updateGroup(params);
  }

  renameGroup(pageId: string, conversationId: string, groupName: string): Observable<void> {
    return this.api.updateGroup({pageId, conversationId, groupName}).pipe(map(() => undefined));
  }

  updateGroupSettings(
    pageId: string,
    groupId: string,
    settings: IZaloPersonalGroupSettingsInput,
  ): Observable<void> {
    return this.api.updateGroup({
      pageId,
      userId: groupId,
      act: 'update_group_settings',
      options: JSON.stringify(settings),
    }).pipe(map(() => undefined));
  }

  changeFriendAlias(params: IChangeFriendAliasParams): Observable<IChangeFriendAliasResponse> {
    return this.api.changeFriendAlias(params);
  }

  disperseGroup(params: IZlpsDisperseGroupParams): Observable<unknown> {
    return this.api.disperseGroup(params);
  }

  deleteChat(params: IZlpsDeleteChatParams): Observable<unknown> {
    return this.api.deleteChat(params);
  }

  /**
   * Kết nối lại tài khoản Zalo cá nhân (FE13 loginZalopersonal act=connect). BE trả nguyên payload
   * provider khi thành công, `{error}` khi lỗi (FE13 mapOriginal ném lỗi theo field này); lỗi HTTP
   * qua ApiService thành `code: 0`. Trả message của provider (có thể rỗng), lỗi thì throw message lỗi.
   */
  reconnect(pageId: string): Observable<string> {
    return this.loginApi.reconnectZaloPersonal({pageId}).pipe(map(response => {
      const result = response as unknown as Record<string, unknown> | null;
      const error = result?.['error'];
      if (error || result?.['code'] === 0) {
        throw new Error(typeof error === 'string' ? error : '');
      }
      const message = result?.['message'];
      return typeof message === 'string' ? message : '';
    }));
  }

  // ── Member helpers ─────────────────────────────────────────────────────────

  /** Chuẩn hoá memberId: bỏ hậu tố `_0`, trim */
  normalizeMemberId(memberId: unknown): string {
    return String(memberId ?? '').replace('_0', '').trim();
  }

  /** Trích danh sách memberId đã normalize từ memVerList */
  extractMemberIds(memVerList: unknown): string[] {
    if (!Array.isArray(memVerList)) return [];
    return memVerList.map(id => this.normalizeMemberId(id)).filter(Boolean);
  }

  /** Build seed members từ customerGroup (showDetail) — luôn có "All" ở đầu */
  buildSeedMembers(customerGroup: any[] = []): IZlpsGroupMember[] {
    const members: IZlpsGroupMember[] = [{userId: '-1', displayName: 'All'}];
    for (const user of customerGroup || []) {
      const member = this.toGroupMember({
        userId: user?.fanpageUserId || user?.userId,
        displayName: user?.name || user?.displayName,
        alias: user?.alias,
        avatar: user?.avatar,
      });
      if (member?.userId) members.push(member);
    }
    return members;
  }

  /** Normalize 1 member object — trả về null nếu không có userId hợp lệ */
  toGroupMember(member: Partial<IZlpsGroupMember> | null | undefined): IZlpsGroupMember | null {
    if (!member) return null;
    const userId = this.normalizeMemberId(
      member.userId ?? member.fanpageUserId ?? member.uid ?? member.groupId
    );
    if (!userId) return null;
    return {
      ...member,
      userId,
      displayName: member.displayName
        || member.fanpageUserName
        || member.name
        || member.groupName
        || (member as any).group_name
        || userId,
      avatar: member.avatar || member.avt || '',
      alias: member.alias ?? '',
    } as IZlpsGroupMember;
  }

  /**
   * Tính danh sách memberId cần fetch từ API (chưa có trong seedMembers).
   * Loại trừ pageZaloId (chính mình) và các member đã biết.
   */
  getMissingMemberIds(
    groupInfo: IZaloPersonalGroupInfo,
    seedMembers: IZlpsGroupMember[],
    pageId: string,
  ): string[] {
    const knownIds = new Set(seedMembers.map(m => this.normalizeMemberId(m.userId ?? m.fanpageUserId)));
    const pageZaloId = this.normalizeMemberId(pageId.replace('zlp_', ''));
    return this.extractMemberIds(groupInfo.memVerList)
      .filter(id => id && id !== pageZaloId && !knownIds.has(id));
  }

  /**
   * Fetch group members theo chunks (tránh payload quá lớn).
   * Trả về danh sách members đã normalize.
   */
  fetchGroupMembersChunked(pageId: string, memberIds: string[], chunkSize = 500): Observable<IZlpsGroupMember[]> {
    if (!memberIds.length) return of([]);
    const chunks = this.chunkArray(memberIds, chunkSize);
    const chunkRequests = chunks.map(chunk =>
      this.getGroupMembers(pageId, chunk).pipe(
        map(members => members.map(m => this.toGroupMember(m)).filter((m): m is IZlpsGroupMember => !!m?.userId))
      )
    );
    // Chạy tuần tự để không overload API
    return new Observable(subscriber => {
      const allMembers: IZlpsGroupMember[] = [];
      let index = 0;
      const processNext = () => {
        if (index >= chunkRequests.length) {
          subscriber.next(allMembers);
          subscriber.complete();
          return;
        }
        chunkRequests[index].subscribe({
          next: members => { allMembers.push(...members); index++; processNext(); },
          error: err => subscriber.error(err),
        });
      };
      processNext();
    });
  }

  private chunkArray<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size));
    }
    return chunks;
  }

  private query(pageId: string, act: string, payload: Record<string, unknown> = {}): Observable<unknown> {
    return this.api.handleUser({pageId, act, ...payload} as any);
  }

  private command(pageId: string, act: string, payload: Record<string, unknown> = {}): Observable<void> {
    return this.query(pageId, act, payload).pipe(map(response => this.assertSuccessful(response)));
  }

  private assertSuccessful(response: unknown): void {
    const error = getZlpsResponseError(response);
    if (!isZlpsResponseSuccessful(response) || error) throw new Error(error || 'ZALO_PERSONAL_ACTION_FAILED');
  }

  private toUser(response: unknown): IZlpsUserData | null {
    return normalizeZlpsUserData(unwrapZlpsResponseData(response));
  }

  private toMembers(response: unknown): IZlpsGroupMember[] {
    return getZlpsResponseItems<unknown>(response)
      .map(value => normalizeZlpsUserData(value) as IZlpsGroupMember)
      .filter(Boolean);
  }

  private toFriendRequest(value: Record<string, unknown>, pageId: string): IZlpsFriendEvent {
    const request = value['fReqInfo'] as Record<string, unknown> | undefined;
    const userId = String(value['userId'] || value['uid'] || '');
    return {
      id: String(value['id'] || userId), pageId, userId,
      name: String(value['displayName'] || value['zaloName'] || value['name'] || ''),
      avatar: String(value['avatar'] || value['avt'] || ''),
      message: String(request?.['message'] || value['message'] || ''),
      // `createdAt` là thời điểm lời mời được tạo từ API. `fReqInfo.time` là
      // trường nội bộ có đơn vị khác nên không dùng để hiển thị.
      createdAt: this.normalizeTimestamp(value['createdAt']),
    };
  }

  /** Zalo trả time bằng epoch seconds, còn Angular DatePipe cần milliseconds. */
  private normalizeTimestamp(value: unknown): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return 0;

    // Mốc 10^11 nhỏ hơn mọi epoch milliseconds hiện đại, nhưng lớn hơn epoch seconds.
    return numericValue < 100_000_000_000 ? numericValue * 1000 : numericValue;
  }
}
